import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button,
  CircularProgress, Alert, InputAdornment
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const AdminLogin = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isLikelyTenantHost = () => {
    const host = (window.location.hostname || '').toLowerCase();
    if (!host || host === 'localhost' || host === '127.0.0.1') return false;
    if (!host.endsWith('.urstore.tw')) return false;
    return !['urstore.tw', 'www.urstore.tw', 'store.urstore.tw'].includes(host);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Tenant subdomain: try tenant-auth first.
      if (isLikelyTenantHost() && username) {
        try {
          const tenantRes = await api.post('/tenant-auth/login', { username, password });
          if (tenantRes.data.success) {
            const { token, admin, tenant } = tenantRes.data.data;
            localStorage.setItem('admin_token', token);
            localStorage.setItem('admin_info', JSON.stringify({ ...admin, tenant }));
            navigate('/admin/dashboard');
            return;
          }
        } catch (_) {
          // Fall through to password-only legacy login.
        }
      }

      // Main site or fallback login path.
      const fallback = await api.post('/admin/login', { password });
      if (fallback.data.success) {
        localStorage.setItem('admin_token', fallback.data.token);
        navigate('/admin/dashboard');
        return;
      }

      setError(t('login.invalid_credentials'));
    } catch (err) {
      setError(err.response?.data?.error || t('login.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
          {t('login.admin_title')}
        </Typography>
        <Typography color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          {t('login.admin_subtitle')}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('login.username_label')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="your@email.com"
            sx={{ mb: 2 }}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            type="password"
            label={t('login.password_label')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || !password}
          >
            {loading ? <CircularProgress size={24} /> : t('login.submit')}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default AdminLogin;
